import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ContainerButtonProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}

const ContainerButton: React.FC<ContainerButtonProps> = React.memo(({ 
  children, 
  icon, 
  className,
  onClick,
  disabled,
  type = "button",
  ...props 
}) => {
  const hasText = useMemo(() => children && String(children).trim() !== '', [children]);
  
  const buttonClassName = useMemo(() => cn(
    "group relative overflow-hidden flex items-center justify-center",
    "rounded-xl font-medium",
    "transition-all duration-300 ease-out",
    "shadow-md hover:shadow-lg",
    "text-xs",
    "backdrop-blur-sm",
    // Size matches the original button (h-8)
    "h-8 min-w-8",
    // Adjust padding based on content
    hasText ? "px-3" : "px-2",
    // Brand gradient styling matching HeaderActionButton primary variant
    "bg-brand-gradient text-white border border-blue-500/20",
    disabled && "opacity-50 pointer-events-none",
    className
  ), [hasText, disabled, className]);
  
  return (
    <motion.button
      whileHover={{ scale: 1.05, y: -1 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      disabled={disabled}
      type={type}
      className={buttonClassName}
    >
      {/* Enhanced Glow effect */}
      <span className="absolute inset-0 w-full h-full">
        <span className="absolute inset-0 w-full h-full opacity-30 mix-blend-soft-light bg-gradient-to-r from-blue-400 to-indigo-500"/>
      </span>
      
      {/* Button shine effect on hover */}
      <span className="absolute inset-0 w-0 h-full bg-white opacity-20 transition-all duration-500 group-hover:w-full"></span>
      
      {/* Content with icon and text */}
      <span className="relative flex items-center justify-center gap-1.5 z-10">
        {icon && <span className="text-current">{icon}</span>}
        {hasText && <span className="whitespace-nowrap font-medium">{children}</span>}
      </span>
    </motion.button>
  );
});

ContainerButton.displayName = 'ContainerButton';

export default ContainerButton; 